import { ResourceCardProps } from "@/common/card-types.ts";
import { RESOURCE_SEARCH_TYPE } from "@modules/resource-search/resource-search-helper.ts";
import AppListApi from "@apis/app.list.api.ts";
import { useRouting } from "@plugins/common/composables/routing.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useRouter } from "vue-router";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import AppDetailContentSubpage from "@modules/app-list/components/AppDetailContentSubpage.vue";
import { AppLibraryResultItem } from "@modules/resource-search/resource-search-types.ts";
import i18n from "@/plugins/i18n";
import { useI18n } from "vue-i18n";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";

export const useAppCard = () => {
  //composables
  const { handleRouteIntervalCheck, urlReady } = useRouting();
  const { passiveApiFailHandler } = useErrorHandler();
  const contentSubpageInstance = useContentSubpageInstance();
  const router = useRouter();
  const { t } = useI18n();

  const populateAppCardProps = (resourceData: AppLibraryResultItem): ResourceCardProps => {
    const isLaunchable = resourceData?.launchable ?? false;
    const isAppEnabled = resourceData?.enabled ?? false;

    return {
      searchType: RESOURCE_SEARCH_TYPE.APP,
      uuid: resourceData.uuid,
      title: resourceData.name,
      metadata: i18n.t("app_card_metadata_label"),
      thumbnail: {
        type: "image",
        value: resourceData.iconUrl,
      },
      showEmbed: false,
      showViewDetails: true,
      showCardTooltipMessage: !isAppEnabled,
      disabled: !isLaunchable || !isAppEnabled,
      ariaLabelConfig: {
        cardComponent: t("search_resource_card_open_in_new_tab_aria_label"),
        viewDetails: t("search_view_details_aria_label", {
          title: resourceData.name,
        }),
      },
    };
  };
  const populateAppCardPropsFromLibrary = (
    resourceData: AppLibraryResultItem,
  ): ResourceCardProps => {
    const isEmbeddable = resourceData?.embeddable ?? false;
    const isAppEnabled = resourceData?.enabled ?? false;

    return {
      searchType: RESOURCE_SEARCH_TYPE.APP,
      uuid: resourceData.uuid,
      title: resourceData.name,
      metadata: i18n.t("app_card_metadata_label"),
      thumbnail: {
        type: "image",
        value: resourceData.iconUrl,
      },
      showEmbed: true,
      showViewDetails: false,
      showCardTooltipMessage: !isAppEnabled,
      disabled: !isEmbeddable || !isAppEnabled,
    };
  };

  const populateAppCardListeners = (
    resourceData: AppLibraryResultItem,
  ): { click: () => void; viewDetails: () => void } => {
    return {
      click: () => handleClick(resourceData),
      viewDetails: () => handleViewDetails(resourceData),
    };
  };

  const handleClick = (resourceData: AppLibraryResultItem): void => {
    const isLaunchable = resourceData?.launchable ?? false;
    const isAppEnabled = resourceData?.enabled ?? false;

    if (!isLaunchable || !isAppEnabled) {
      return;
    }

    handleRouteIntervalCheck();

    AppListApi.fetchIndependentAppContext({
      params: {
        appUuid: resourceData?.uuid,
        type: "LAUNCH_APP",
      },
    }).then((response) => {
      if (response.code === "ok") {
        const result = response.data;
        urlReady.value = result.launchUrl;

        // Track app launch event AFTER guard check
        userActivityTrackEvent.launchApp({
          appUuid: resourceData.uuid,
          appName: resourceData.name ?? "",
        });
      } else {
        const routeData = router.resolve({
          name: "Forbidden",
        });
        urlReady.value = routeData.href;
      }
    }, passiveApiFailHandler);
  };

  const handleViewDetails = (resourceData: AppLibraryResultItem): void => {
    contentSubpageInstance.show(AppDetailContentSubpage, {
      uuid: resourceData?.uuid,
      hasParentSubpage: false,
    });
  };

  return {
    populateAppCardProps,
    populateAppCardPropsFromLibrary,
    populateAppCardListeners,
    handleViewDetails,
  };
};
